/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable no-use-before-define */
import type { ReactNode } from 'react'
import { useEffect, useReducer, useCallback, createContext, useContext, useMemo } from 'react'

import Cookies from 'js-cookie'
import jwtDecode from 'jwt-decode'

import {
  TUCKERTUB_AUTH_SESSION_ID,
  WOOCOMMERCE_TUCKERTUB_LOCALSTORAGE_CUSTOMER_INFO
} from '~/constants'
import { useAuthV2 } from '~/hooksV2'

interface IAuthContextStateModifiers {
  login: (payload: any) => void
  logout: () => void
  isLoggedIn: () => boolean
  getUser: () => void
  getLastOrderByUserId:( userId:any )=> void
}
interface IAuthContextStateValues {
  user: any
  lastOrder:any
  isAuthenticated: boolean
}

type InitialState = IAuthContextStateModifiers & IAuthContextStateValues

const stateModifiers: IAuthContextStateModifiers = {
  login: () => {},
  logout: () => {},
  getLastOrderByUserId:( userId:any )=>{},
  isLoggedIn: () => {
    return false
  },
  getUser: () => {}
}

const stateValues: IAuthContextStateValues = {
  user: null,
  lastOrder:null,
  isAuthenticated: false
}

const initialState: InitialState = {
  ...stateValues,
  ...stateModifiers
}

const AuthContext = createContext<InitialState>(initialState)

export const useAuth = () => {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error('useAuth must be used within a AuthProvider')
  }

  return context
}

interface Action {
  type: 'LOGOUT' | 'LOGIN' | 'GET_USER' | 'SET_LAST_ORDER'
  payload?: any
}

function authReducer(state: IAuthContextStateValues, action: Action) {
  // console.log('Set auth state', state)
  // console.log('Set auth payload', action.payload)
  switch (action.type) {
    case 'LOGOUT':
      //console.log('LOGOUT running...')

      Cookies.remove(TUCKERTUB_AUTH_SESSION_ID)
      localStorage.removeItem(WOOCOMMERCE_TUCKERTUB_LOCALSTORAGE_CUSTOMER_INFO)

      return {
        ...state,
        lastOrder:null,
        user: null,
        isAuthenticated: false
      }
    case 'LOGIN':
      //console.log('LOGIN running...')

      return {
        ...state,
        user: action.payload,
        isAuthenticated: true
      }
    case 'SET_LAST_ORDER':
        //console.log('LOGIN running...')
  
        return {
          ...state,
          lastOrder: action.payload,
    }
    case 'GET_USER':
      //console.log('GET_USER running...')

      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload
      }

    default:
      return state
  }
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider = (props: AuthProviderProps) => {
  const { children } = props
  const [state, dispatch] = useReducer(authReducer, initialState)
  const { user } = useAuthV2()


  const login = useCallback((payload:any) => {
    // console.log('Login payload', payload)
    let decoded = {}
    //console.log('payloadlogin', payload)

    if (typeof payload === 'string' && payload?.startsWith('ey')) {
      decoded = jwtDecode(payload)

      return dispatch({
        type: 'LOGIN',
        payload: decoded
      })
    }

    if (payload.jwt) {
      decoded = jwtDecode(payload.jwt)

      return dispatch({
        type: 'LOGIN',
        payload: decoded
      })
    }

    dispatch({
      type: 'LOGIN',
      payload
    })
  }, [])


  const setLastOrder = useCallback((payload:any) => {
    // console.log('Login payload', payload)
    return dispatch({
      type: 'SET_LAST_ORDER',
      payload
    })
  }, [])

  const logout = useCallback(() => {
    dispatch({ type: 'LOGOUT' })
  }, [])

  const isLoggedIn = useCallback(() => {
    return !!state.user
  }, [state.user])

  const getUser = useCallback(() => {
    dispatch({ type: 'GET_USER' })
  }, [])

  useEffect(() => {
    // console.log('useEffectUser', state.user)
    // console.log('useEffectIsAuthenticated', state.isAuthenticated)
  }, [state.user])

  const getLastOrderByUserId = async ( userId:any )=> {
    const response = await fetch(`${process.env.NEXT_PUBLIC_FRONTEND_URL}/api/my-order`,
        {
          method: 'POST',
          body: JSON.stringify({ customer_id: userId }),
          headers: {
            'Content-Type': 'application/json'
          }
        }
      )
  
      const data:any = await response.json()
      const order = data?.data || []
      if(order){
        setLastOrder(order?.[0])
      }else{
        setLastOrder(null)
      }
   }

   
  useEffect(()=>{
    if(user?.databaseId){
      getLastOrderByUserId(user?.databaseId)
    }else{
      setLastOrder(null) 
    }
  },[user?.databaseId])

  useEffect(() => {
    if (Cookies.get(TUCKERTUB_AUTH_SESSION_ID)) {
      validateJWT()
    }

    async function validateJWT() {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_FRONTEND_URL}/api/auth/verify-jwt-token`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            token: Cookies.get(TUCKERTUB_AUTH_SESSION_ID)
          })
        }
      )
      const data = await response.json()

      //console.log('validateJWT data', data)

      if (data.error) {
        return logout()
      }

      login(data)

      //console.log('ValidateJWT effect', data)
    }
  }, [])

  const memoizedContextValues = useMemo(() => {
    return {
      ...state,
      getLastOrderByUserId,
      login,
      logout,
      isLoggedIn,
      getUser,
      user: state.user,
      isAuthenticated: state.isAuthenticated
    }
  }, [state, login, logout, isLoggedIn, getUser, getLastOrderByUserId])

  return <AuthContext.Provider value={memoizedContextValues}>{children}</AuthContext.Provider>
}
